<template>
    <div class="flex p-4 rounded-lg bg-gray-50 mb-6">
        <div class="mr-4 shrink-0">
            <UserAvatar :user="{id: 1, first_name: 'Ben', last_name: 'Leah', avatar_image: ''}" :internal="true" />
        </div>
        <div>
            <h4 class="text-md font-semibold text-gray-900">Ben Leah</h4>
            <p class="mt-1 text-sm text-gray-700">Having trouble? Submit a support ticket with as much detail as possible. Ideally, fill out the form on the page where you had the problem because it automatically attaches a few extra details to help me solve the problem.</p>
        </div>
    </div>
    <form action="#" @submit.prevent="save">
        <div class="min-w-0 flex-1 px-0 md:grid md:grid-cols-2 md:gap-4">
            <div class="col-span-2">
                <FormField :field="form.support.fields.title" namespace="support" name="title" class="mb-2" />
            </div>
<!--            <div class="col-span-2">-->
<!--                <FormField :field="form.support.fields.category" namespace="support" name="category" class="mb-2" />-->
<!--            </div>-->
            <div class="col-span-2">
                <FormField :field="form.support.fields.action" namespace="support" name="action" class="mb-2" />
            </div>
<!--            <div class="col-span-2">-->
<!--                <FormField :field="form.support.fields.expected_behaviour" namespace="support" name="expected_behaviour" class="mb-2" />-->
<!--            </div>-->
            <div class="col-span-2">
                <FormField :field="form.support.fields.observed_behaviour" namespace="support" name="observed_behaviour" class="mb-2" />
            </div>
            <div class="col-span-2">
                <FormField :field="form.support.fields.creator_notes" namespace="support" name="creator_notes" class="mb-2" />
            </div>
            <div class="col-span-2">
                <FormFileUploadDropZone @files-dropped="addFiles" class="mb-4">
                    <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <div class="flex text-sm text-gray-600">
                        <label for="support-ticket-media" class="relative cursor-pointer rounded-md font-medium text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 hover:text-blue-500">
                            <span>Upload a screenshot</span>
                            <input id="support-ticket-media" name="support-ticket-media" type="file" class="sr-only" multiple v-on:change="onInputChange" accept="image/png, image/jpeg">
                        </label>
                        <p class="pl-1">or drag and drop</p>
                    </div>
                    <p class="text-xs text-gray-500">PNG, JPG up to 10MB</p>
                </FormFileUploadDropZone>
            </div>
            <div class="col-span-2">
                <Button type="submit" width="w-auto" :disabled="form.support.loading">
                    <span>{{ form.support.loading ? 'Submitting' : 'Submit' }}</span>
                    <LoadingIcon v-if="form.support.loading" class="ml-2 h-5 w-5 animate-reverse-spin" />
                </Button>
            </div>
        </div>
    </form>
    <FormFileUploadQueue :files="files" class="mt-6" :removeFile="removeFile" media_type="image" />
</template>

<script>
import { reactive, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSupportStore } from '@sto/support.store'
import { useSystemStore } from '@sto/system.store'
import {isValid, getPayload, removeExtension, sessionInformation, isJsonString} from '@lib/helpers'
import useFileList from '@lib/file-list'
import * as Sentry from "@sentry/browser";

import LoadingIcon from '@ico/LoadingIcon.vue'
import UserAvatar from "@par/user/UserAvatar.vue"

export default {
    components: {
        LoadingIcon,
        UserAvatar
    },
    setup (props) {

        const ROUTE = useRoute()
        const ROUTER = useRouter()
        const SUPPORT_STORE = useSupportStore()
        const SYSTEM_STORE = useSystemStore()

        const { files, addFiles, removeFile, resetFileQueue } = useFileList()

        const uploadable_files = computed(() => {
            if (!Array.isArray(files.value)) return []
            return files.value.filter(f => f.status == 'ready') || []
        })

        const state = reactive({
            support_ticket_id: null
        })

        const form = reactive({
            support: {
                loading: false,
                fields: {
                    title: {
                        active: true,
                        label: 'Subject',
                        type: 'text',
                        value: null,
                        stored_value: null,
                        initial_value: null,
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [
                            { type: 'max-char', value: 250 },
                        ],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    category: {
                        active: false,
                        label: 'Which best describes your issue?',
                        type: 'select',
                        options: [
                            { value: '', label: 'Please select whichever is closest...' },
                            { value: '', label: 'I was trying to save something and there was an error' },
                            { value: '', label: 'Some data is missing or incorrect' },
                            { value: '', label: 'A feature that I usually use has vanished' },
                            { value: '', label: 'Something else' },
                        ],
                        value: null,
                        stored_value: null,
                        initial_value: null,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [
                            { type: 'max-char', value: 1000 },
                        ],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    action: {
                        active: true,
                        label: 'What were you trying to do?',
                        type: 'textarea',
                        value: null,
                        stored_value: null,
                        initial_value: null,
                        required: true,
                        pristine: true,
                        valid: false,
                        rows: 2,
                        message: '',
                        validation: [
                            { type: 'max-char', value: 1000 },
                        ],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    observed_behaviour: {
                        active: true,
                        label: 'What went wrong?',
                        type: 'textarea',
                        value: null,
                        stored_value: null,
                        initial_value: null,
                        rows: 2,
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [
                            { type: 'max-char', value: 1000 },
                        ],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    expected_behaviour: {
                        active: false,
                        label: 'Expected behaviour',
                        type: 'textarea',
                        value: null,
                        stored_value: null,
                        initial_value: null,
                        rows: 2,
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [
                            { type: 'max-char', value: 1000 },
                        ],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    creator_notes: {
                        active: true,
                        label: 'Anything else we should know (optional)',
                        type: 'textarea',
                        value: null,
                        stored_value: null,
                        required: false,
                        rows: 2,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [
                            { type: 'max-char', value: 1000 },
                        ],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    route_path: {
                        active: true,
                        type: 'hidden',
                        value: null,
                        initial_value: null,
                        validation: []
                    },
                    route_name: {
                        active: true,
                        type: 'hidden',
                        value: null,
                        initial_value: null,
                        validation: []
                    },
                    route_params: {
                        active: true,
                        type: 'hidden',
                        value: null,
                        initial_value: null,
                        validation: []
                    },
                    client_environment: {
                        active: true,
                        type: 'hidden',
                        value: null,
                        initial_value: null,
                        validation: []
                    }
                }
            }
        })

        const onInputChange = e => {
            addFiles(e.target.files)
            e.target.value = null
        }

        const timeout = ms => {
            return new Promise(resolve => setTimeout(resolve, ms));
        }

        const uploadFile = async (file, index) => {
            // make sure we're not trying to re-upload existing images
            if (file.status == 'complete' || !state.support_ticket_id) return

            await timeout(1000 * index)


            file.status = 'loading'
            const payload = {
                file: file.file,
                file_name: removeExtension(file.file.name),
                type: 'image'
            }
            try {
                const upload = await SUPPORT_STORE.addMedia(state.support_ticket_id, payload)
                file.status = 'complete'
            } catch (error) {
                console.error('AssetService.addMedia error', error)
                file.status = 'error'
            }
        }

        const uploadFiles = async () => {
            const uploads = await Promise.all(files.value.map((file, index) => uploadFile(file, index)))
            resetFileQueue()
        }

        const getClientEnvironment = async () => {
            const client_info = await sessionInformation()
            if (!client_info) return null
            return JSON.stringify(client_info)
        }

        const getRouteParams = () => {
            return ROUTE.params
                ? JSON.stringify(ROUTE.params)
                : null
        }

        const getRouteName = () => {
            return ROUTE.name
        }

        const getRoutePath = () => {
            return ROUTE.fullPath
        }

        const setEnvironmentFields = async () => {
            form.support.fields.client_environment.value = await getClientEnvironment()
            form.support.fields.route_params.value = getRouteParams()
            form.support.fields.route_name.value = getRouteName()
            form.support.fields.route_path.value = getRoutePath()
        }

        const resetForm = () => {
            for (let field in form.support.fields) {
                form.support.fields[field].value = form.support.fields[field].initial_value
                if (form.support.fields[field].hasOwnProperty('pristine')) form.support.fields[field].pristine = true
                if (form.support.fields[field].hasOwnProperty('valid')) form.support.fields[field].valid = false
            }
            state.support_ticket_id = null
            setEnvironmentFields()
        }

        const createTicket = async payload => {
            return SUPPORT_STORE.create(payload)
        }

        const save = async () => {

            if (form.support.loading || !isValid(form.support.fields)) return

            try {
                // set the loading flag
                form.support.loading = true

                const ticket_payload = getPayload(form.support.fields)
                const support_ticket = await createTicket(ticket_payload)
                state.support_ticket_id = support_ticket?.id || null
                if (uploadable_files.value.length > 0) await uploadFiles()

                SYSTEM_STORE.addSuccess('Support Ticket Submitted!', 'We\'ll review and get back to you soon')
                resetForm()

            } catch (error) {
                console.error('save error', error)
                SYSTEM_STORE.addError('Submit error!', error?.message || 'There was an error saving your ticket')

            } finally {
                form.support.loading = false
            }
        }

        onMounted(() => {
            setEnvironmentFields()
        })

        return {
            state,
            form,
            save,
            onInputChange,
            addFiles,
            uploadFiles,
            removeFile,
            files,
            uploadable_files,
        }
    }
}
</script>